import React, { useEffect, useState } from "react";
import { BellRing, X, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { getPushStatus, enableOfficePush, sendTestPush } from "../lib/webpush";

const DISMISS_KEY = "mm_alerts_nudge_dismissed_at";
const DISMISS_DAYS = 3;

/** Amber banner shown on Admin/Kitchen when THIS device has push alerts off —
 *  the #1 reason the owner misses new-order notifications. */
export default function OfficeAlertsNudge() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_DAYS * 86400000) return;
    } catch { /* ignore */ }
    getPushStatus().then((s) => {
      if (s === "off" || s === "needs-install" || s === "denied") setShow(s);
    });
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enableOfficePush();
      const r = await sendTestPush();
      toast({ title: "Order alerts on", description: `This device now buzzes on every new order & reservation. Test sent to ${r.devices} device${r.devices === 1 ? "" : "s"}.` });
      setShow(false);
    } catch (e) {
      toast({ title: "Couldn't enable alerts", description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch (err) { console.warn("localStorage write failed", err); }
    setShow(false);
  };

  if (!show) return null;
  const denied = show === "denied";
  const needsInstall = show === "needs-install";
  return (
    <div
      data-testid="alerts-nudge-banner"
      className="mb-8 flex flex-wrap items-center gap-3 border border-amber-500/60 bg-amber-500/10 px-4 py-3"
    >
      <BellRing size={16} className="text-amber-400 shrink-0" />
      <p className="text-sm text-[var(--mm-cream)] flex-1 min-w-[220px]">
        <span className="font-semibold text-amber-300">This device isn&apos;t getting order alerts.</span>{" "}
        {denied
          ? "Notifications are blocked for this site — allow them in your browser settings, then reload."
          : needsInstall
            ? "On iPad/iPhone: tap Share → Add to Home Screen, open the Office app, then enable alerts."
            : "Turn on push notifications so new orders buzz you the moment they're paid."}
      </p>
      {!denied && !needsInstall && (
        <button
          onClick={enable}
          disabled={busy}
          data-testid="alerts-nudge-enable"
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400 text-black text-[0.7rem] tracking-[0.2em] uppercase font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />}
          Enable alerts
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="alerts-nudge-dismiss"
        aria-label="Dismiss"
        className="w-8 h-8 flex items-center justify-center text-[var(--mm-cream-muted)] hover:text-[var(--mm-cream)] transition-colors"
      >
        <X size={15} />
      </button>
    </div>
  );
}
